/**
 * Available Capacity Report — full page, booking-driven.
 * Access Evo Design System compliant.
 *
 * Shows unbooked hours by resource, role, skill and location.
 * Primary workflow: Skill filter + availability threshold + Request button.
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import {
  format,
  addDays,
  addWeeks,
  startOfWeek,
  endOfWeek,
  eachWeekOfInterval,
  max as dateMax,
  min as dateMin,
  parseISO,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';
import {
  Users,
  ChevronUp,
  ChevronDown,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  ExternalLink,
  Calendar,
  SlidersHorizontal,
  Settings2,
  RotateCcw,
  GripVertical,
  X,
  Loader2,
} from 'lucide-react';

import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetHeader, SheetFooter } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Check, Filter } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { useScheduler, getDefaultStatusColor } from '@/contexts/SchedulerContext';
import { useScreenData } from '@/hooks/useScreenData';
import { useCompany } from '@/contexts/CompanyContext';
import { ComprehensiveBookingModal } from '@/components/calendar/ComprehensiveBookingModal';
import { DataModeToggle } from '@/components/DataModeToggle';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpButton } from '@/components/help/HelpButton';
import { bookingsApi } from '@/services/api';
import { mapApiBookingToBooking } from '@/hooks/use-date-range-bookings';
import { usePermissions } from '@/contexts/PermissionsContext';
import { normalizeDurationType } from '@/utils/heatmap-utils';
import type { Resource, Booking, SchedulerGroup, AppSettings, WorkingHoursProfile } from '@/types';

// ─── Colour resolvers ─────────────────────────────────────────────────────────

function resolveToken(token: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return v || fallback;
}

const TEAL        = () => resolveToken('--teal-500',         'oklch(0.50 0.0896 194.8)');
const PLANNED_BLUE_FALLBACK = () => getDefaultStatusColor('planned');
const AVAIL_GREY  = () => resolveToken('--muted-foreground', 'oklch(0.50 0.0186 248.7)');

// ─── Constants ────────────────────────────────────────────────────────────────

const WORKING_HOURS_PER_DAY = 8;

// ─── Types ────────────────────────────────────────────────────────────────────

type PeriodKey = 'next2w' | 'next4w' | 'next8w' | 'next3m' | 'next6m';
type ViewBy = 'individual' | 'team' | 'department' | 'location';
type AvailabilityThreshold = 'any' | '20' | '40' | '60' | 'full';

export interface CapacityRow {
  id: string;
  name: string;
  role: string;
  department: string;   // org department (r.department)
  team: string;         // scheduler group (r.schedulerGroup)
  location: string;
  skills: string[];
  initials: string;
  photoUrl?: string;
  availableFromDate: Date | null; // null = available now
  availableHours: number;
  availablePct: number;
  confirmedBookedPct: number;
  plannedPct: number;
  totalCapacityHours: number;
  billingRate?: number;
  periodBookings: Array<{
    id: string;
    projectName: string;
    startDate: string;
    endDate: string;
    allocationPct: number;
    status: 'confirmed' | 'planned';
  }>;
  weeklyAvailability: number[]; // % available per week (forward-looking)
}

// Grouped (aggregated) row — used when viewBy !== 'individual'
export interface GroupedCapacityRow {
  groupKey: string;           // department name, location, team name, etc.
  resourceCount: number;
  availableResourceCount: number;
  totalCapacityHours: number;
  availableHours: number;
  availablePct: number;       // weighted average
  confirmedBookedPct: number;
  plannedPct: number;
  skills: string[];           // union of all skills in group
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = String(name ?? '').trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function workingDaysInInterval(start: Date, end: Date): number {
  let count = 0;
  const cursor = new Date(start);
  while (cursor < end) {
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

const WEEK_DAY_KEYS: Array<keyof WorkingHoursProfile> = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

function profileHoursForDay(dayOfWeek: number, profile: WorkingHoursProfile): number {
  return profile[WEEK_DAY_KEYS[dayOfWeek]] ?? 0;
}

function getEffectiveProfile(
  resource: Resource,
  globalProfile: WorkingHoursProfile,
): WorkingHoursProfile {
  if (resource.useCustomWorkingProfile && resource.customWorkingProfile) {
    return resource.customWorkingProfile;
  }
  return globalProfile;
}

function workingHoursInInterval(start: Date, end: Date, profile: WorkingHoursProfile): number {
  let total = 0;
  const cursor = new Date(start);
  while (cursor < end) {
    total += profileHoursForDay(cursor.getDay(), profile);
    cursor.setDate(cursor.getDate() + 1);
  }
  return total;
}

function resourceCapacityHoursInRange(
  resource: Resource,
  start: Date,
  end: Date,
  globalProfile?: WorkingHoursProfile,
): number {
  const profile = resource.useCustomWorkingProfile && resource.customWorkingProfile
    ? resource.customWorkingProfile
    : globalProfile;
  if (!profile) {
    return workingDaysInInterval(start, end) * WORKING_HOURS_PER_DAY;
  }
  return workingHoursInInterval(start, end, profile);
}

function resolveBookingEnd(booking: Booking, bStart: Date, bEnd: Date): Date {
  const dt = normalizeDurationType(booking.durationType);
  if (dt === 'hourly') return bEnd;
  if (bEnd.getTime() > bStart.getTime()) return bEnd;

  // endTime <= startTime: derive end from durationDays/durationHours when
  // available so that multi-day bookings are not silently truncated to 1 day.
  if (booking.durationDays && booking.durationDays > 0) {
    return addDays(startOfDay(bStart), Math.ceil(booking.durationDays));
  }
  if (booking.durationHours && booking.durationHours > WORKING_HOURS_PER_DAY) {
    return addDays(startOfDay(bStart), Math.ceil(booking.durationHours / WORKING_HOURS_PER_DAY));
  }
  // Fallback: treat as a single full day
  return addDays(startOfDay(bStart), 1);
}

function bookingHoursInRange(booking: Booking, rangeStart: Date, rangeEnd: Date, profile: WorkingHoursProfile): number {
  const bStart = parseISO(booking.startTime);
  let bEnd     = parseISO(booking.endTime);

  // For day-based bookings where endTime <= startTime (e.g. single-day or
  // multi-day bookings stored with the same timestamp for start and end),
  // derive the correct end date from durationDays/durationHours.
  bEnd = resolveBookingEnd(booking, bStart, bEnd);

  const overlapStart = dateMax([bStart, rangeStart]);
  const overlapEnd   = dateMin([bEnd,   rangeEnd]);
  if (overlapStart >= overlapEnd) return 0;

  const dt = normalizeDurationType(booking.durationType);

  // Hourly bookings: use actual time span pro-rated for overlap
  if (dt === 'hourly') {
    const bookingMs = bEnd.getTime() - bStart.getTime();
    if (bookingMs > 0) {
      const totalHours = bookingMs / (1000 * 60 * 60);
      const overlapMs  = overlapEnd.getTime() - overlapStart.getTime();
      return totalHours * (overlapMs / bookingMs);
    }
    // Zero-duration fallback: treat as full-day so the booking is not
    // silently ignored (matches calculatePeriodUtilization behaviour).
    return profileHoursForDay(overlapStart.getDay(), profile);
  }

  // Single-day overlap: use profile hours for that day.
  if (isSameDay(overlapStart, overlapEnd)) {
    return profileHoursForDay(overlapStart.getDay(), profile) * durationFactor(dt);
  }

  // Multi-day: sum profile hours per day with exclusive end boundary.
  // Subtract 1ms so that midnight boundaries don't spill into the next day.
  const inclusiveEnd = new Date(overlapEnd.getTime() - 1);
  if (inclusiveEnd < overlapStart) return 0;
  let total = 0;
  const cursor = new Date(overlapStart);
  while (cursor <= inclusiveEnd) {
    total += profileHoursForDay(cursor.getDay(), profile);
    cursor.setDate(cursor.getDate() + 1);
  }
  return total * durationFactor(dt);
}

function durationFactor(dt: 'full-day' | 'half-day' | 'half-day-am' | 'half-day-pm' | 'hourly'): number {
  switch (dt) {
    case 'half-day':
    case 'half-day-am':
    case 'half-day-pm':
      return 0.5;
    default:
      return 1;
  }
}

function computeDateRange(p: PeriodKey): { from: Date; to: Date } {
  const today = startOfDay(new Date());
  switch (p) {
    case 'next2w': return { from: today, to: addWeeks(today, 2) };
    case 'next4w': return { from: today, to: addWeeks(today, 4) };
    case 'next8w': return { from: today, to: addWeeks(today, 8) };
    case 'next3m': return { from: today, to: addWeeks(today, 12) };
    case 'next6m': return { from: today, to: addWeeks(today, 24) };
  }
}

function formatDateRange(from: Date, to: Date): string {
  return `${format(from, 'dd MMM yyyy')} — ${format(to, 'dd MMM yyyy')}`;
}

// ─── Data hook ────────────────────────────────────────────────────────────────

function useAvailableCapacityData(
  dateFrom: Date,
  dateTo: Date,
  selectedDepartments: string[],
  selectedTeams: string[],
  selectedRoles: string[],
  selectedSkills: string[],
  selectedLocations: string[],
  availabilityThreshold: AvailabilityThreshold,
  viewBy: ViewBy,
  resources: Resource[],
  bookings: Booking[],
  schedulerGroups: SchedulerGroup[],
  settings?: AppSettings,
) {

  return useMemo(() => {
    // Build authoritative resourceId → groupName lookup from the company's
    // schedulerGroups. This is the single source of truth for which resources
    // belong to which planner group for the selected company.
    const resourceGroupMap = new Map<string, string>();
    for (const group of schedulerGroups) {
      for (const rid of group.resourceIds) {
        resourceGroupMap.set(rid, group.name);
      }
    }

    const realResources = resources.filter(
      (r) => r.id !== 'unallocated' && r.status !== 'inactive',
    );

    const filtered = realResources.filter((r) => {
      if (selectedDepartments.length > 0 && !selectedDepartments.includes(r.department ?? '')) return false;
      if (selectedTeams.length > 0) {
        const resourceGroup = resourceGroupMap.get(r.id) ?? '';
        if (!selectedTeams.includes(resourceGroup)) return false;
      }
      if (selectedRoles.length > 0 && !selectedRoles.includes(r.role ?? '')) return false;
      if (selectedLocations.length > 0 && !selectedLocations.includes(r.location ?? '')) return false;
      if (selectedSkills.length > 0) {
        const rSkills = (r.skills ?? []).map((s) => s.name.toLowerCase());
        if (!selectedSkills.some((s) => rSkills.includes(s.toLowerCase()))) return false;
      }
      return true;
    });

    // Pre-bucket bookings by resourceId AND resourceIds (multi-resource bookings)
    const bookingsByResource: Record<string, Booking[]> = {};
    for (const b of bookings) {
      const seenForBooking = new Set<string>();
      const rid = b.resourceId ?? '';
      if (rid && rid !== 'unallocated') {
        seenForBooking.add(rid);
        if (!bookingsByResource[rid]) bookingsByResource[rid] = [];
        bookingsByResource[rid].push(b);
      }
      if (b.resourceIds) {
        for (const id of b.resourceIds) {
          if (id && !seenForBooking.has(id)) {
            seenForBooking.add(id);
            if (!bookingsByResource[id]) bookingsByResource[id] = [];
            bookingsByResource[id].push(b);
          }
        }
      }
    }

    const globalProfile = settings?.globalWorkingProfile;

    const DEFAULT_WORKING_PROFILE: WorkingHoursProfile = {
      monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8,
      saturday: 0, sunday: 0,
    };
    const resolvedGlobalProfile: WorkingHoursProfile = globalProfile ?? DEFAULT_WORKING_PROFILE;

    // Build weekly forward look (up to 8 weeks)
    const today = startOfDay(new Date());
    const weekStarts = eachWeekOfInterval(
      { start: dateFrom, end: dateTo },
      { weekStartsOn: 1 },
    ).slice(0, 8);

    const rows: CapacityRow[] = filtered.map((r) => {
      const profile = getEffectiveProfile(r, resolvedGlobalProfile);
      const totalCapacityHours = resourceCapacityHoursInRange(r, dateFrom, dateTo, globalProfile);
      const resourceBookings = (bookingsByResource[r.id] ?? []).filter(
        (b) => b.status !== 'cancelled',
      );

      let confirmedHours = 0;
      let plannedHours   = 0;

      const periodBookings: CapacityRow['periodBookings'] = resourceBookings
        .filter((b) => {
          const bStart = parseISO(b.startTime);
          const bEnd   = resolveBookingEnd(b, bStart, parseISO(b.endTime));
          return bStart < dateTo && bEnd > dateFrom;
        })
        .flatMap((b) => {
          const hrs = bookingHoursInRange(b, dateFrom, dateTo, profile);
          if (hrs <= 0) return [];
          const isConfirmed = b.status === 'confirmed' || b.status === 'completed';
          if (isConfirmed) confirmedHours += hrs;
          else             plannedHours   += hrs;
          const bStartParsed = parseISO(b.startTime);
          const bEndResolved = resolveBookingEnd(b, bStartParsed, parseISO(b.endTime));
          const bookingCapacity = workingHoursInInterval(bStartParsed, bEndResolved, profile);
          const allocationPct = bookingCapacity > 0
            ? Math.round((hrs / bookingCapacity) * 100)
            : 0;
          if (allocationPct === 0) return [];
          return [{
            id: b.id,
            projectName: b.projectName,
            startDate: format(bStartParsed, 'dd MMM yyyy'),
            endDate:   format(parseISO(b.endTime), 'dd MMM yyyy'),
            allocationPct,
            status: (isConfirmed ? 'confirmed' : 'planned') as 'confirmed' | 'planned',
          }];
        });

      const bookedHours    = confirmedHours + plannedHours;
      const availableHours = Math.max(0, totalCapacityHours - bookedHours);
      const availablePct   = totalCapacityHours > 0
        ? Math.round((availableHours / totalCapacityHours) * 100)
        : 0;
      const confirmedBookedPct = totalCapacityHours > 0
        ? Math.round((confirmedHours / totalCapacityHours) * 100)
        : 0;
      const plannedPct = totalCapacityHours > 0
        ? Math.round((plannedHours / totalCapacityHours) * 100)
        : 0;

      // Determine "available from" — find first date with capacity
      let availableFromDate: Date | null = null;
      if (availableHours > 0) {
        // Check if they have bookings that start in the future covering today
        const hasBookingNow = resourceBookings.some((b) => {
          const bStart = parseISO(b.startTime);
          const bEnd   = resolveBookingEnd(b, bStart, parseISO(b.endTime));
          return bStart <= today && bEnd > today;
        });
        if (!hasBookingNow) {
          availableFromDate = null; // available now
        } else {
          // Find when current commitments end
          const lastEnd = resourceBookings
            .filter((b) => {
              const bStart = parseISO(b.startTime);
              const bEnd = resolveBookingEnd(b, bStart, parseISO(b.endTime));
              return bEnd > today;
            })
            .reduce<Date | null>((max, b) => {
              const bStart = parseISO(b.startTime);
              const bEnd = resolveBookingEnd(b, bStart, parseISO(b.endTime));
              return max === null || bEnd > max ? bEnd : max;
            }, null);
          availableFromDate = lastEnd;
        }
      }

      // Weekly availability sparkline
      const weeklyAvailability = weekStarts.map((ws) => {
        const we     = endOfWeek(ws, { weekStartsOn: 1 });
        const wCap   = resourceCapacityHoursInRange(r, ws, we, globalProfile);
        if (wCap === 0) return 0;
        let wBooked  = 0;
        for (const b of resourceBookings) {
          wBooked += bookingHoursInRange(b, ws, we, profile);
        }
        return Math.round((Math.max(0, wCap - wBooked) / wCap) * 100);
      });

      return {
        id:                  r.id,
        name:                r.name,
        role:                r.role ?? '—',
        department:          r.department ?? '—',
        team:                resourceGroupMap.get(r.id) ?? '—',
        location:            r.location ?? '—',
        skills:              (r.skills ?? []).map((s) => s.name),
        initials:            getInitials(r.name),
        photoUrl:            r.photoUrl,
        availableFromDate,
        availableHours,
        availablePct,
        confirmedBookedPct,
        plannedPct,
        totalCapacityHours,
        billingRate:         r.billingRate,
        periodBookings,
        weeklyAvailability,
      };
    });

    // Apply availability threshold filter using exact (non-rounded) percentage
    // to avoid rounding artifacts where e.g. 59.5% rounds to 60% passing >= 60.
    const thresholdFiltered = rows.filter((r) => {
      const exactPct = r.totalCapacityHours > 0
        ? (r.availableHours / r.totalCapacityHours) * 100
        : 0;
      switch (availabilityThreshold) {
        case '20':   return exactPct >= 20;
        case '40':   return exactPct >= 40;
        case '60':   return exactPct >= 60;
        case 'full': return exactPct >= 95;
        default:     return true;
      }
    });

    // Skills with zero available resources
    const allSkills = new Set<string>();
    rows.forEach((r) => r.skills.forEach((s) => allSkills.add(s)));
    const skillsWithNoAvailability: string[] = [];
    allSkills.forEach((skill) => {
      const hasAvailable = rows.some(
        (r) => r.skills.includes(skill) && r.availablePct > 0,
      );
      if (!hasAvailable) skillsWithNoAvailability.push(skill);
    });

    // Summary stats
    const totalResources     = thresholdFiltered.length;
    const availableResources = thresholdFiltered.filter((r) => r.availableHours > 0).length;
    const fullyAvailable     = thresholdFiltered.filter((r) => r.availablePct >= 95).length;
    const totalAvailableHours = thresholdFiltered.reduce((s, r) => s + r.availableHours, 0);

    // ── Grouped rows — aggregate by viewBy key
    const groupedRows: GroupedCapacityRow[] = (() => {
      if (viewBy === 'individual') return [];
      const getKey = (r: CapacityRow): string => {
        if (viewBy === 'department') return r.department !== '—' ? r.department : 'Unassigned';
        if (viewBy === 'location')   return r.location   !== '—' ? r.location   : 'Unassigned';
        if (viewBy === 'team') {
          return r.team !== '—' ? r.team : 'Unassigned';
        }
        return r.department !== '—' ? r.department : 'Unassigned';
      };

      const groupMap: Map<string, CapacityRow[]> = new Map();
      for (const row of thresholdFiltered) {
        const key = getKey(row);
        if (!groupMap.has(key)) groupMap.set(key, []);
        groupMap.get(key)!.push(row);
      }

      return Array.from(groupMap.entries()).map(([groupKey, members]) => {
        const totalCap   = members.reduce((s, r) => s + r.totalCapacityHours, 0);
        const totalAvail = members.reduce((s, r) => s + r.availableHours, 0);
        const totalConf  = members.reduce((s, r) => s + (r.confirmedBookedPct / 100) * r.totalCapacityHours, 0);
        const totalPlan  = members.reduce((s, r) => s + (r.plannedPct / 100) * r.totalCapacityHours, 0);
        const skillSet   = new Set<string>();
        members.forEach((r) => r.skills.forEach((s) => skillSet.add(s)));
        return {
          groupKey,
          resourceCount:          members.length,
          availableResourceCount: members.filter((r) => r.availableHours > 0).length,
          totalCapacityHours:     totalCap,
          availableHours:         Math.round(totalAvail),
          availablePct:           totalCap > 0 ? Math.round((totalAvail / totalCap) * 100) : 0,
          confirmedBookedPct:     totalCap > 0 ? Math.round((totalConf  / totalCap) * 100) : 0,
          plannedPct:             totalCap > 0 ? Math.round((totalPlan  / totalCap) * 100) : 0,
          skills:                 Array.from(skillSet).slice(0, 10),
        };
      }).sort((a, b) => b.availablePct - a.availablePct);
    })();

    return {
      rows: thresholdFiltered,
      groupedRows,
      allRows: rows,
      skillsWithNoAvailability,
      summary: {
        totalResources,
        availableResources,
        fullyAvailable,
        totalAvailableHours,
      },
      isEmpty: thresholdFiltered.length === 0,
      hasNoCompanyData: realResources.length === 0,
    };
  }, [
    resources,
    bookings,
    dateFrom,
    dateTo,
    selectedDepartments,
    selectedTeams,
    selectedRoles,
    selectedSkills,
    selectedLocations,
    availabilityThreshold,
    viewBy,
    schedulerGroups,
    settings,
  ]);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// Segmented control
interface SegmentedProps<T extends string> {
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}
function Segmented<T extends string>({ options, value, onChange }: SegmentedProps<T>) {
  return (
    <div className="inline-flex items-center bg-muted rounded-md p-[3px] gap-[2px]">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={[
            'px-2.5 h-7 rounded text-xs font-medium transition-all whitespace-nowrap',
            o.value === value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          ].join(' ')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Sortable column header
interface SortHeaderProps {
  label: string;
  col: string;
  sortCol: string;
  sortDir: 'asc' | 'desc';
  onSort: (col: string) => void;
  align?: 'left' | 'right';
  minWidth?: number;
}
function SortHeader({ label, col, sortCol, sortDir, onSort, align = 'left', minWidth }: SortHeaderProps) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className="cursor-pointer select-none px-3 py-2.5 font-medium text-[12px] text-muted-foreground whitespace-nowrap group"
      style={{ textAlign: align, minWidth }}
    >
      <span
        className="inline-flex items-center gap-1"
        style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}
      >
        {label}
        <span className={active ? 'text-foreground' : 'opacity-0 group-hover:opacity-50'}>
          {active && sortDir === 'asc' ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </span>
      </span>
    </th>
  );
}

// Stat card — matches UtilisationReport pattern exactly
type StatCardAccent = 'teal' | 'warning' | 'destructive' | 'default';

const accentClasses: Record<StatCardAccent, { value: string }> = {
  teal:        { value: 'text-[var(--accent-teal-900)] dark:text-[var(--accent-teal-500)]' },
  warning:     { value: 'text-[var(--accent-warning-900)] dark:text-[var(--accent-warning-500)]' },
  destructive: { value: 'text-destructive' },
  default:     { value: 'text-foreground' },
};

interface StatCardProps {
  value: string;
  accent?: StatCardAccent;
  label: string;
  subLabel: string;
  isFilterable?: boolean;
  isActive?: boolean;
  onClick?: () => void;
}
function StatCard({ value, accent = 'default', label, subLabel, isFilterable, isActive, onClick }: StatCardProps) {
  const classes = accentClasses[accent];
  return (
    <div
      className={[
        'flex-1 flex flex-col gap-1 rounded-[var(--radius-md)] border bg-card px-4 py-4 min-w-0 transition-all duration-150',
        isFilterable ? 'cursor-pointer select-none hover:border-primary/50 hover:shadow-sm' : '',
        isActive
          ? 'border-primary ring-2 ring-primary/20 shadow-sm'
          : 'border-border',
      ].join(' ')}
      role={isFilterable ? 'button' : undefined}
      tabIndex={isFilterable ? 0 : undefined}
      aria-pressed={isFilterable ? isActive : undefined}
      onClick={isFilterable ? onClick : undefined}
      onKeyDown={
        isFilterable
          ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-1">
        <span className={`text-2xl font-semibold leading-none ${classes.value}`}>
          {value}
        </span>
        {isFilterable && isActive && (
          <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary/10 text-primary shrink-0 mt-0.5">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </span>
        )}
      </div>
      <span className="text-xs text-muted-foreground mt-1">{label}</span>
      <span className="text-xs text-muted-foreground">{subLabel}</span>
      {isFilterable && !isActive && (
        <span className="text-[10px] text-primary/70 mt-1 font-medium">Click to filter ↓</span>
      )}
      {isFilterable && isActive && (
        <span className="text-[10px] text-primary mt-1 font-medium">Filtering active · click to clear</span>
      )}
    </div>
  );
}

// Legend swatch
function LegendSwatch({ colour, label, dashed }: { colour: string; label: string; dashed?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      {dashed ? (
        <svg width="16" height="4" className="shrink-0">
          <line x1="0" y1="2" x2="16" y2="2" stroke={colour} strokeWidth="1.5" strokeDasharray="3 2" />
        </svg>
      ) : (
        <span className="inline-block h-3 w-3 rounded-[2px] shrink-0" style={{ background: colour }} />
      )}
      {label}
    </span>
  );
}

// Chart tooltip
function CapacityChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: CapacityRow & { topSkills: string[] } }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  const confirmed = payload.find((p) => p.name === 'confirmed')?.value ?? 0;
  const planned   = payload.find((p) => p.name === 'planned')?.value ?? 0;
  const available = payload.find((p) => p.name === 'available')?.value ?? 0;
  const topSkills: string[] = row?.topSkills ?? [];
  return (
    <div
      className="rounded-md shadow-md text-[12px] p-3 flex flex-col gap-1 min-w-[200px]"
      style={{
        background: 'var(--popover)',
        border: '0.5px solid var(--border)',
        color: 'var(--popover-foreground)',
      }}
    >
      <p className="font-semibold mb-1">{label}</p>
      <p>Confirmed booked: <strong>{confirmed}%</strong></p>
      <p>Planned booked: <strong>{planned}%</strong></p>
      <p className="text-teal-500 font-medium">Available: <strong>{available}%</strong></p>
      <p>Available hours: <strong>{row?.availableHours ?? 0}h</strong></p>
      {topSkills.length > 0 && (
        <p className="text-muted-foreground mt-1">Skills: {topSkills.slice(0, 3).join(', ')}</p>
      )}
    </div>
  );
}

// "Available from" cell
function AvailableFromCell({ date }: { date: Date | null }) {
  if (date === null) {
    return (
      <span className="inline-flex items-center gap-1 text-[var(--accent-teal-900)] dark:text-[var(--accent-teal-500)] font-medium text-[12px]">
        <span className="h-1.5 w-1.5 rounded-full bg-teal-500 shrink-0" />
        Now
      </span>
    );
  }
  return (
    <span className="text-[12px] text-foreground whitespace-nowrap">
      {format(date, 'dd MMM yyyy')}
    </span>
  );
}

// Available % badge
function AvailPctBadge({ pct }: { pct: number }) {
  if (pct >= 80)
    return <Badge variant="success" accent="success" className="text-[11px] px-2 py-0.5">{pct}%</Badge>;
  if (pct >= 40)
    return <Badge variant="warning" accent="warning" className="text-[11px] px-2 py-0.5">{pct}%</Badge>;
  if (pct > 0)
    return <Badge variant="outline" className="text-[11px] px-2 py-0.5 text-muted-foreground">{pct}%</Badge>;
  return <Badge variant="destructive" accent="destructive" className="text-[11px] px-2 py-0.5">0%</Badge>;
}

// Booked % badge — inverse colour coding (high booking = red, low = muted)
function BookedPctBadge({ pct, variant = 'confirmed' }: { pct: number; variant?: 'confirmed' | 'planned' }) {
  if (pct === 0)
    return <span className="text-[13px] text-muted-foreground">0%</span>;
  if (variant === 'planned')
    // Planned is always shown muted — it's soft risk
    return <Badge variant="outline" className="text-[11px] px-2 py-0.5 text-muted-foreground">{pct}%</Badge>;
  // Confirmed: colour by booking pressure (inverse of availability)
  if (pct >= 80)
    return <Badge variant="destructive" accent="destructive" className="text-[11px] px-2 py-0.5">{pct}%</Badge>;
  if (pct >= 50)
    return <Badge variant="warning" accent="warning" className="text-[11px] px-2 py-0.5">{pct}%</Badge>;
  return <Badge variant="outline" className="text-[11px] px-2 py-0.5 text-muted-foreground">{pct}%</Badge>;
}

// Booking status pill
function BookingStatusPill({ status }: { status: 'confirmed' | 'planned' }) {
  if (status === 'confirmed') {
    return <Badge variant="success" accent="success" className="text-[10px] px-1.5 py-0 capitalize">Confirmed</Badge>;
  }
  return <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground capitalize">Planned</Badge>;
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({
  onReset,
  onBenchReport,
  viewBy,
  hasNoCompanyData,
}: {
  onReset: () => void;
  onBenchReport?: () => void;
  viewBy?: ViewBy;
  hasNoCompanyData?: boolean;
}) {
  const viewLabel: Record<ViewBy, string> = {
    individual: 'individual resources',
    team:       'planner groups',
    department: 'departments',
    location:   'locations',
  };

  const title = hasNoCompanyData
    ? `No ${viewBy ? viewLabel[viewBy] : 'data'} available for this company`
    : 'No available resources match your filters';

  const description = hasNoCompanyData
    ? 'The selected company does not have any resource data. Try switching to a different company or check that resources have been set up for this company.'
    : 'Try adjusting the filters, availability threshold, or date range.';

  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
      <div
        className="flex items-center justify-center h-14 w-14 rounded-2xl"
        style={{ background: 'var(--muted)' }}
      >
        <Users className="h-7 w-7 text-muted-foreground" />
      </div>
      <p className="text-[15px] font-medium text-foreground">
        {title}
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs">
        {description}
      </p>
      {!hasNoCompanyData && (
        <div className="flex items-center gap-4 mt-1">
          <button
            onClick={onReset}
            className="text-[13px] text-primary hover:underline cursor-pointer"
          >
            Reset filters
          </button>
          {onBenchReport && (
            <button
              onClick={onBenchReport}
              className="inline-flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground hover:underline cursor-pointer"
            >
              View bench report
              <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Resource detail drawer ───────────────────────────────────────────────────

function ResourceDetailDrawer({
  row,
  open,
  onClose,
  onNavigateToCalendar,
}: {
  row: CapacityRow | null;
  open: boolean;
  onClose: () => void;
  onNavigateToCalendar?: () => void;
}) {
  if (!row) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="p-0 flex flex-col gap-0 overflow-hidden w-full sm:max-w-[520px]"
      >
        <SheetTitle className="sr-only">{row.name} — Available Capacity Detail</SheetTitle>
        <SheetDescription className="sr-only">
          Availability breakdown, skills and bookings for {row.name}.
        </SheetDescription>

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar className="h-11 w-11 shrink-0">
              <AvatarImage src={row.photoUrl} alt={row.name} />
              <AvatarFallback className="text-sm font-semibold bg-primary/10 text-primary">
                {row.initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-semibold text-sm text-foreground truncate">{row.name}</p>
              <p className="text-xs text-muted-foreground truncate">
                {row.role}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {row.department}{row.location !== '—' ? ` · ${row.location}` : ''}
              </p>
            </div>
          </div>
          <div className="text-right shrink-0 ml-3">
            <p className="text-2xl font-semibold leading-none text-[var(--accent-teal-900)] dark:text-[var(--accent-teal-500)]">
              {row.availablePct}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">Available</p>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">

          {/* Section 1 — Availability breakdown */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">Availability breakdown</p>
            <div className="flex flex-col gap-3">
              {/* Available bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Available</span>
                  <span className="font-semibold text-teal-500">{row.availablePct}%</span>
                </div>
                <Progress
                  value={row.availablePct}
                  className="h-2"
                  style={{ '--progress-color': 'var(--teal-500)', background: 'var(--muted)' } as React.CSSProperties}
                />
              </div>
              {/* Confirmed booked bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Confirmed booked</span>
                  <span className="font-semibold text-foreground">{row.confirmedBookedPct}%</span>
                </div>
                <Progress
                  value={row.confirmedBookedPct}
                  className="h-2"
                  style={{ background: 'var(--muted)' } as React.CSSProperties}
                />
              </div>
              {/* Planned bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Planned</span>
                  <span className="font-semibold text-muted-foreground">{row.plannedPct}%</span>
                </div>
                <Progress
                  value={row.plannedPct}
                  className="h-2"
                  style={{ background: 'var(--muted)' } as React.CSSProperties}
                />
              </div>

              {/* Stat chips */}
              <div className="flex gap-2 mt-1">
                {[
                  { label: 'Available', value: `${row.availableHours}h` },
                  { label: 'Confirmed',  value: `${Math.round(row.totalCapacityHours * row.confirmedBookedPct / 100)}h` },
                  { label: 'Planned',    value: `${Math.round(row.totalCapacityHours * row.plannedPct / 100)}h` },
                ].map((chip) => (
                  <div
                    key={chip.label}
                    className="flex-1 rounded-md px-2.5 py-2 flex flex-col gap-0.5 text-center bg-muted border border-border"
                  >
                    <span className="text-sm font-semibold text-foreground">{chip.value}</span>
                    <span className="text-xs text-muted-foreground">{chip.label}</span>
                  </div>
                ))}
              </div>

              {/* Available from */}
              <div className="flex items-center gap-2 mt-1">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground">Available from:</span>
                <AvailableFromCell date={row.availableFromDate} />
              </div>
            </div>
          </div>

          {/* Section 2 — Week by week availability */}
          {row.weeklyAvailability.length > 0 && (
            <div className="px-5 py-4 border-b">
              <p className="text-xs font-semibold text-foreground mb-3">Week-by-week availability</p>
              <div className="flex gap-1.5">
                {row.weeklyAvailability.map((pct, i) => (
                  <div key={i} className="flex flex-col items-center gap-1 flex-1">
                    <div className="w-full rounded-sm overflow-hidden" style={{ height: 40, background: 'var(--muted)' }}>
                      <div
                        className="w-full transition-all duration-300 rounded-sm"
                        style={{
                          height: `${pct}%`,
                          marginTop: `${100 - pct}%`,
                          background: pct >= 60 ? 'var(--teal-500)' : pct >= 20 ? '#EF9F27' : 'var(--destructive)',
                          opacity: 0.8,
                        }}
                      />
                    </div>
                    <span className="text-[9px] text-muted-foreground">{`W${i + 1}`}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Teal = 60%+ free · Amber = 20–59% · Red = &lt;20% available
              </p>
            </div>
          )}

          {/* Section 3 — Skills */}
          {row.skills.length > 0 && (
            <div className="px-5 py-4 border-b">
              <p className="text-xs font-semibold text-foreground mb-3">Skills & profile</p>
              <div className="flex flex-wrap gap-1.5">
                {row.skills.map((skill) => (
                  <Badge key={skill} variant="outline" className="text-[11px] px-2 py-0.5 text-foreground">
                    {skill}
                  </Badge>
                ))}
              </div>
              {row.billingRate != null && row.billingRate > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Day rate:</span>
                  <span className="text-xs font-semibold text-foreground">
                    £{row.billingRate.toLocaleString()}/day
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Section 4 — Current & upcoming bookings */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">Current & upcoming bookings</p>
            {row.periodBookings.length === 0 ? (
              <p className="text-xs text-muted-foreground">No bookings in this period — fully available.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {row.periodBookings.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-start justify-between gap-2 rounded-md border border-border px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{b.projectName}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {b.startDate} – {b.endDate}
                      </p>
                      <p className="text-xs text-muted-foreground">{b.allocationPct}% allocation</p>
                    </div>
                    <BookingStatusPill status={b.status} />
                  </div>
                ))}
              </div>
            )}

            {onNavigateToCalendar && (
              <button
                onClick={onNavigateToCalendar}
                className="inline-flex items-center gap-1 mt-3 text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                View in calendar planner
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Section 5 — Open demand match */}
          <div className="px-5 py-4">
            <p className="text-xs font-semibold text-foreground mb-3">Open demand match</p>
            <div
              className="rounded-md border border-border px-3 py-3 text-center"
              style={{ background: 'var(--muted)' }}
            >
              <p className="text-xs text-muted-foreground">
                No active open demand requests matching this resource's role and availability at this time.
              </p>
            </div>
          </div>

        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface AvailableCapacityReturnContext {
  /** The page key to navigate back to after saving a booking */
  returnToPage: string;
  /** Optional project ID to pre-populate on the booking form */
  projectId?: string;
  /** Optional role name to pre-populate on the booking form */
  roleName?: string;
}

export function AvailableCapacity({
  onNavigateToBench,
  onNavigateToCalendar,
  onNavigate,
  returnContext,
}: {
  onNavigateToBench?: () => void;
  onNavigateToCalendar?: () => void;
  /** Generic navigation callback — matches App.tsx `setCurrentPage` signature */
  onNavigate?: (page: string) => void;
  /** When set, the booking modal will pre-populate with this context and navigate back on save */
  returnContext?: AvailableCapacityReturnContext;
} = {}) {
  const { permissions, hasFullAccess } = usePermissions();
  const canExport = hasFullAccess || (permissions?.reportAccessPermissions?.availableCapacityReport?.allowExport ?? false);

  // ── Data mode (useScreenData auto-switches between mockData and liveData)
  const {
    resources,
    bookings: contextBookings,
    projectDetails,
    costCentres,
    settings,
    schedulerGroups,
    isDemo,
    currentMode,
    addBooking: contextAddBooking,
    updateBooking: contextUpdateBooking,
    deleteBooking: contextDeleteBooking,
  } = useScreenData('calendar');

  const { isLoading: isContextLoading } = useScheduler();
  const { currentCompany, isLoading: isCompanyLoading } = useCompany();
  const companyIdRef = useRef(currentCompany?.id);
  companyIdRef.current = currentCompany?.id;
  const prevCompanyIdRef = useRef(currentCompany?.id);

  // ── Period / date state
  const defaultPeriod: PeriodKey = 'next4w';
  const { from: defaultFrom, to: defaultTo } = computeDateRange(defaultPeriod);
  const [period, setPeriod]    = useState<PeriodKey>(defaultPeriod);
  const [dateFrom, setDateFrom] = useState<Date>(defaultFrom);
  const [dateTo, setDateTo]     = useState<Date>(defaultTo);

  // ── Live bookings (fetched from API for the report date range)
  // liveData.bookings is intentionally empty — bookings aren't bulk-loaded
  // in live mode, so we fetch them ourselves for the report's date range.
  const [liveReportBookings, setLiveReportBookings] = useState<Booking[]>([]);
  const [hasFetchedBookings, setHasFetchedBookings] = useState(false);
  const [liveBookingsError, setLiveBookingsError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fetchIdRef = useRef(0);

  const handlePeriodChange = useCallback((p: PeriodKey) => {
    const { from, to } = computeDateRange(p);
    setPeriod(p);
    setDateFrom(from);
    setDateTo(to);
    setPage(1);
  }, []);

  // ── Filter state
  const [viewBy, setViewBy]                          = useState<ViewBy>('individual');
  const [availThreshold, setAvailThreshold]          = useState<AvailabilityThreshold>('any');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams]            = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles]            = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills]          = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations]    = useState<string[]>([]);
  const [filterDrawerOpen,   setFilterDrawerOpen]    = useState(false);

  // ── Reset all filters, live bookings and pagination when company changes
  // so that stale selections from a previous company don't persist.
  useEffect(() => {
    const prevId = prevCompanyIdRef.current;
    prevCompanyIdRef.current = currentCompany?.id;
    if (prevId === undefined || prevId === currentCompany?.id) return;

    setSelectedDepartments([]);
    setSelectedTeams([]);
    setSelectedRoles([]);
    setSelectedSkills([]);
    setSelectedLocations([]);
    setActiveCardFilter(null);
    setSortCol('available-pct');
    setSortDir('desc');
    setPage(1);
    setChartSortBy('available-desc');
    setChartShowLimit(25);
    setLiveReportBookings([]);
    setHasFetchedBookings(false);
    setLiveBookingsError(null);
  }, [currentCompany?.id]);

  // ── Card filters
  const [activeCardFilter, setActiveCardFilter]      = useState<'fully-available' | null>(null);

  // ── Sort + pagination
  const [sortCol, setSortCol]   = useState<string>('available-pct');
  const [sortDir, setSortDir]   = useState<'asc' | 'desc'>('desc');
  const [page, setPage]         = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // ── Column visibility
  const [colConfigOpen, setColConfigOpen] = useState(false);
  type CapacityColKey = 'department' | 'role' | 'location' | 'availableFrom' | 'availableHrs' | 'availablePct' | 'confirmedPct' | 'plannedPct';
  const ALL_CAPACITY_COLS: { key: CapacityColKey; label: string; description: string }[] = [
    { key: 'department',   label: 'Department',    description: 'Resource department'                      },
    { key: 'role',         label: 'Role',          description: 'Resource role or job title'               },
    { key: 'location',     label: 'Location',      description: 'Resource location'                        },
    { key: 'availableFrom', label: 'Available from', description: 'Date resource becomes available'        },
    { key: 'availableHrs', label: 'Avail. hrs',    description: 'Total available hours in period'          },
    { key: 'availablePct', label: 'Avail. %',      description: 'Availability percentage'                  },
    { key: 'confirmedPct', label: 'Confirmed %',   description: 'Confirmed booking percentage'             },
    { key: 'plannedPct',   label: 'Planned %',     description: 'Planned booking percentage'               },
  ];
  const [visibleCapacityCols, setVisibleCapacityCols] = useState<Set<CapacityColKey>>(
    new Set(['department','role','location','availableFrom','availableHrs','availablePct','confirmedPct','plannedPct'])
  );
  const toggleCapacityCol = (key: CapacityColKey) => setVisibleCapacityCols(prev => {
    const next = new Set(prev);
    if (next.has(key)) { if (next.size > 1) next.delete(key); } else next.add(key);
    return next;
  });
  const resetCapacityCols = () => setVisibleCapacityCols(new Set(['department','role','location','availableFrom','availableHrs','availablePct','confirmedPct','plannedPct']));

  // ── Chart controls
  const [chartSortBy, setChartSortBy]   = useState<'available-desc' | 'available-asc' | 'name'>('available-desc');
  const [chartShowLimit, setChartShowLimit] = useState<number>(25);

  // ── Drawer / booking modal state
  const [drawerRow, setDrawerRow]             = useState<CapacityRow | null>(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookingResource, setBookingResource]   = useState<Resource | null>(null);

  // ── Skills gap section ref
  const skillsGapRef = useRef<HTMLDivElement>(null);

  // ── Chart container width measurement
  const chartScrollRef = useRef<HTMLDivElement>(null);

  // ── Fetch live bookings for the report's date range.
  // Uses the same useCallback + ref pattern as useForecastData so that
  // the fetch function is stable and the effect only re-fires when the
  // meaningful dependencies (mode, dates, company) change.
  const fetchLiveBookings = useCallback(async (start: Date, end: Date) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const id = ++fetchIdRef.current;
    setLiveBookingsError(null);
    try {
      // Extend end by 1 day to ensure bookings on the last day of the range
      // are returned regardless of timezone offset between client and server.
      const fetchEnd = addDays(end, 1);
      const apiBookings = await bookingsApi.getByDateRange(
        start.toISOString(),
        fetchEnd.toISOString(),
        false,
        companyIdRef.current,
      );
      if (abortRef.current?.signal.aborted) return;
      if (fetchIdRef.current === id) {
        const mapped = apiBookings.map(mapApiBookingToBooking);
        setLiveReportBookings(mapped);
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Failed to fetch live bookings for capacity report:', message, err);
      if (fetchIdRef.current === id) {
        setLiveBookingsError(message);
        setLiveReportBookings([]);
      }
    } finally {
      if (fetchIdRef.current === id) setHasFetchedBookings(true);
    }
  }, []);

  // Reset fetch-tracking flag when leaving live mode so the next
  // live switch correctly shows a loading state.
  useEffect(() => {
    if (currentMode !== 'live') {
      setHasFetchedBookings(false);
    }
  }, [currentMode]);

  useEffect(() => {
    if (currentMode !== 'live') return;
    if (isCompanyLoading || !currentCompany?.id) return;
    fetchLiveBookings(dateFrom, dateTo);
    return () => { abortRef.current?.abort(); };
  }, [currentMode, dateFrom, dateTo, fetchLiveBookings, currentCompany?.id, isCompanyLoading]);

  const bookings = currentMode === 'live' ? liveReportBookings : contextBookings;

  const addBooking = contextAddBooking;
  const updateBooking = contextUpdateBooking;
  const deleteBooking = contextDeleteBooking;

  // True while either resources or bookings are still loading in live mode.
  // Uses hasFetchedBookings (not a transient loading flag) so the spinner
  // stays visible from mode-switch until the first API response arrives.
  const isLiveDataLoading = currentMode === 'live' && (isContextLoading || !hasFetchedBookings);

  // ── Data
  const data = useAvailableCapacityData(
    dateFrom, dateTo,
    selectedDepartments, selectedTeams,
    selectedRoles, selectedSkills, selectedLocations,
    availThreshold,
    viewBy,
    resources,
    bookings,
    schedulerGroups,
    settings,
  );

  // ── Filter options derived from resources

  // Department options (from r.department)
  const departmentOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        const dept = r.department ?? '';
        if (dept && !seen.has(dept)) {
          seen.add(dept);
          acc.push({ value: dept, label: dept });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  // Planner Group options — derived from the company-filtered schedulerGroups list
  // rather than from resource fields, to prevent cross-company group data from
  // appearing when resources have stale schedulerGroup values.
  const teamsEnabled = settings.groupingMethod === 'scheduler-group' && schedulerGroups.length > 0;
  const teamOptions = useMemo(() => {
    if (!teamsEnabled) return [];
    return schedulerGroups
      .map((g) => ({ value: g.name, label: g.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [schedulerGroups, teamsEnabled]);

  // Reset viewBy to 'individual' if the user was viewing by planner group
  // but the current company does not have planner groups configured.
  useEffect(() => {
    if (viewBy === 'team' && !teamsEnabled) {
      setViewBy('individual');
    }
  }, [teamsEnabled, viewBy]);

  const roleOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        if (r.role && !seen.has(r.role)) { seen.add(r.role); acc.push({ value: r.role, label: r.role }); }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  const skillOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .flatMap((r) => r.skills ?? [])
      .reduce<Array<{ value: string; label: string }>>((acc, s) => {
        if (!seen.has(s.name)) { seen.add(s.name); acc.push({ value: s.name, label: s.name }); }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  const locationOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated' && r.location)
      .reduce<Array<{ value: string; label: string }>>((acc, r) => {
        if (!seen.has(r.location!)) { seen.add(r.location!); acc.push({ value: r.location!, label: r.location! }); }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);



  // ── Apply card filter on top of data
  // individual rows after card filter
  const cardFilteredRows = useMemo(() => {
    if (activeCardFilter === 'fully-available') return data.rows.filter((r) => r.availablePct >= 95);
    return data.rows;
  }, [data.rows, activeCardFilter]);

  // grouped rows — already fully filtered inside the hook
  const displayGrouped = viewBy !== 'individual';
  const groupLabel: Record<ViewBy, string> = {
    individual: 'Resource',
    team:       'Planner Group',
    department: 'Department',
    location:   'Location',
  };

  // ── Chart data
  const chartData = useMemo(() => {
    if (displayGrouped) {
      const sorted = [...data.groupedRows].sort((a, b) => {
        switch (chartSortBy) {
          case 'available-desc': return b.availablePct - a.availablePct;
          case 'available-asc':  return a.availablePct - b.availablePct;
          case 'name':           return a.groupKey.localeCompare(b.groupKey);
          default:               return b.availablePct - a.availablePct;
        }
      });
      const limited = chartShowLimit === -1 ? sorted : sorted.slice(0, chartShowLimit);
      return limited.map((g) => {
        // Clamp segments so they always sum to exactly 100 — prevents bars overflowing the chart
        const confirmed  = Math.min(Math.max(g.confirmedBookedPct, 0), 100);
        const planned    = Math.min(Math.max(g.plannedPct, 0), 100 - confirmed);
        const available  = Math.max(100 - confirmed - planned, 0);
        return {
          name:           g.groupKey,
          confirmed,
          planned,
          available,
          topSkills:      g.skills.slice(0, 3),
          availableHours: g.availableHours,
        };
      });
    }
    const rows = [...cardFilteredRows].sort((a, b) => {
      switch (chartSortBy) {
        case 'available-desc': return b.availablePct - a.availablePct;
        case 'available-asc':  return a.availablePct - b.availablePct;
        case 'name':           return a.name.localeCompare(b.name);
        default:               return b.availablePct - a.availablePct;
      }
    });
    const limited = chartShowLimit === -1 ? rows : rows.slice(0, chartShowLimit);
    return limited.map((r) => {
      // Clamp segments so they always sum to exactly 100 — prevents bars overflowing the chart
      const confirmed  = Math.min(Math.max(r.confirmedBookedPct, 0), 100);
      const planned    = Math.min(Math.max(r.plannedPct, 0), 100 - confirmed);
      const available  = Math.max(100 - confirmed - planned, 0);
      return {
        name:           r.name.split(' ')[0],
        confirmed,
        planned,
        available,
        topSkills:      r.skills.slice(0, 3),
        availableHours: r.availableHours,
      };
    });
  }, [displayGrouped, data.groupedRows, cardFilteredRows, chartSortBy, chartShowLimit]);

  // ── Responsive chart sizing
  const X_AXIS_WIDTH = 140;
  const BAR_HEIGHT   = 28;
  const chartInnerHeight = Math.max(280, chartData.length * (BAR_HEIGHT + 8) + 40);

  // ── Sort + paginate table
  const sortedRows = useMemo(() => {
    if (displayGrouped) {
      // Sort grouped rows
      return [...data.groupedRows].sort((a, b) => {
        let va: string | number = '';
        let vb: string | number = '';
        switch (sortCol) {
          case 'name':          va = a.groupKey;              vb = b.groupKey;              break;
          case 'available-pct': va = a.availablePct;          vb = b.availablePct;          break;
          case 'available-hrs': va = a.availableHours;        vb = b.availableHours;        break;
          case 'confirmed-pct': va = a.confirmedBookedPct;    vb = b.confirmedBookedPct;    break;
          case 'planned-pct':   va = a.plannedPct;            vb = b.plannedPct;            break;
          default:              va = a.availablePct;          vb = b.availablePct;
        }
        if (typeof va === 'string') {
          return sortDir === 'asc'
            ? (va as string).localeCompare(vb as string)
            : (vb as string).localeCompare(va as string);
        }
        return sortDir === 'asc'
          ? (va as number) - (vb as number)
          : (vb as number) - (va as number);
      }) as (CapacityRow | GroupedCapacityRow)[];
    }
    return [...cardFilteredRows].sort((a, b) => {
      const ar = a as CapacityRow;
      const br = b as CapacityRow;
      let va: string | number = '';
      let vb: string | number = '';
      switch (sortCol) {
        case 'name':          va = ar.name;               vb = br.name;               break;
        case 'department':    va = ar.department;          vb = br.department;          break;
        case 'role':          va = ar.role;               vb = br.role;               break;
        case 'location':      va = ar.location;           vb = br.location;           break;
        case 'available-pct': va = ar.availablePct;       vb = br.availablePct;       break;
        case 'available-hrs': va = ar.availableHours;     vb = br.availableHours;     break;
        case 'confirmed-pct': va = ar.confirmedBookedPct; vb = br.confirmedBookedPct; break;
        case 'planned-pct':   va = ar.plannedPct;         vb = br.plannedPct;         break;
        case 'available-from': {
          const da = ar.availableFromDate?.getTime() ?? 0;
          const db = br.availableFromDate?.getTime() ?? 0;
          va = da; vb = db; break;
        }
        default: va = ar.name; vb = br.name;
      }
      if (typeof va === 'string') {
        return sortDir === 'asc'
          ? (va as string).localeCompare(vb as string)
          : (vb as string).localeCompare(va as string);
      }
      return sortDir === 'asc'
        ? (va as number) - (vb as number)
        : (vb as number) - (va as number);
    }) as (CapacityRow | GroupedCapacityRow)[];
  }, [displayGrouped, data.groupedRows, cardFilteredRows, sortCol, sortDir]);

  const totalPages    = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const paginatedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = useCallback((col: string) => {
    if (col === sortCol) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir(col === 'available-pct' || col === 'available-hrs' ? 'desc' : 'asc');
    }
    setPage(1);
  }, [sortCol]);

  const handleReset = useCallback(() => {
    handlePeriodChange(defaultPeriod);
    setViewBy('individual');
    setAvailThreshold('any');
    setSelectedDepartments([]);
    setSelectedTeams([]);
    setSelectedRoles([]);
    setSelectedSkills([]);
    setSelectedLocations([]);

    setActiveCardFilter(null);
    setSortCol('available-pct');
    setSortDir('desc');
    setPage(1);
    setChartSortBy('available-desc');
    setChartShowLimit(25);
  }, [handlePeriodChange]);

  // ── Request booking — opens booking modal pre-populated with resource
  const handleRequestBooking = useCallback((row: CapacityRow, e: React.MouseEvent) => {
    e.stopPropagation(); // prevent row click / drawer open
    const resource = resources.find((r) => r.id === row.id) ?? null;
    setBookingResource(resource);
    setBookingModalOpen(true);
  }, [resources]);

  // ── Export to Excel
  const handleExportExcel = useCallback(() => {
    if (!data.rows.length) {
      toast.error('Nothing to export', { description: 'No data matches the current filters.' });
      return;
    }
    const dateLabel = `${format(dateFrom, 'dd MMM yyyy')} – ${format(dateTo, 'dd MMM yyyy')}`;
    const summaryRows = data.rows.map((r) => ({
      'Resource':            r.name,
      'Role':                r.role,
      'Department':          r.department,
      'Location':            r.location,
      'Skills':              r.skills.join(', '),
      'Period':              dateLabel,
      'Available from':      r.availableFromDate ? format(r.availableFromDate, 'dd MMM yyyy') : 'Now',
      'Available hours':     r.availableHours,
      'Available %':         r.availablePct,
      'Confirmed booked %':  r.confirmedBookedPct,
      'Planned %':           r.plannedPct,

    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(summaryRows);
    ws['!cols'] = Object.keys(summaryRows[0]).map((k) => ({
      wch: Math.max(k.length, ...summaryRows.map((r) => String(r[k as keyof typeof r]).length)) + 2,
    }));
    XLSX.utils.book_append_sheet(wb, ws, 'Available Capacity');
    const filename = `available-capacity-${format(dateFrom, 'yyyy-MM-dd')}-to-${format(dateTo, 'yyyy-MM-dd')}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success('Export complete', {
      description: `${data.rows.length} resource${data.rows.length !== 1 ? 's' : ''} exported to Excel.`,
    });
  }, [data.rows, dateFrom, dateTo]);

  // ── Footer totals
  const footerTotals = useMemo(() => {
    if (displayGrouped) {
      const rows = data.groupedRows;
      if (rows.length === 0) return null;
      return {
        totalAvailHours: rows.reduce((s, r) => s + r.availableHours, 0),
        avgAvailPct:     Math.round(rows.reduce((s, r) => s + r.availablePct, 0) / rows.length),
        avgConfirmedPct: Math.round(rows.reduce((s, r) => s + r.confirmedBookedPct, 0) / rows.length),
        avgPlannedPct:   Math.round(rows.reduce((s, r) => s + r.plannedPct, 0) / rows.length),
      };
    }
    const rows = data.rows;
    if (rows.length === 0) return null;
    return {
      totalAvailHours: rows.reduce((s, r) => s + r.availableHours, 0),
      avgAvailPct:     Math.round(rows.reduce((s, r) => s + r.availablePct, 0) / rows.length),
      avgConfirmedPct: Math.round(rows.reduce((s, r) => s + r.confirmedBookedPct, 0) / rows.length),
      avgPlannedPct:   Math.round(rows.reduce((s, r) => s + r.plannedPct, 0) / rows.length),
    };
  }, [displayGrouped, data.groupedRows, data.rows]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-6 py-4 shrink-0"
        style={{ borderBottom: '0.5px solid var(--color-border-tertiary, var(--border))' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <Users className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <Breadcrumb
              items={[{ label: 'Reports' }, { label: 'Available capacity' }]}
              className="text-[12px] text-muted-foreground mb-0.5"
            />
            <h1 className="text-xl font-semibold text-foreground leading-tight">
              Available capacity
            </h1>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              Unbooked hours by resource, role, skill and location — booking-driven
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-4">
          <DataModeToggle screen="calendar" />
          <FeatureFeedbackCollector feature="Available Capacity" />
          <HelpButton />
        </div>
      </div>

      {/* ── Filters bar ──────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 flex-wrap px-4 py-3 shrink-0"
        style={{
          background: 'var(--color-background-secondary, var(--muted))',
          borderBottom: '0.5px solid var(--color-border-tertiary, var(--border))',
        }}
      >
        {/* Period */}
        <Select value={period} onValueChange={(v) => handlePeriodChange(v as PeriodKey)}>
          <SelectTrigger className="h-8 text-[13px] w-auto min-w-[140px] shrink-0 bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="next2w">Next 2 weeks</SelectItem>
            <SelectItem value="next4w">Next 4 weeks</SelectItem>
            <SelectItem value="next8w">Next 8 weeks</SelectItem>
            <SelectItem value="next3m">Next 3 months</SelectItem>
            <SelectItem value="next6m">Next 6 months</SelectItem>
          </SelectContent>
        </Select>

        {/* Date range display */}
        <div
          className="inline-flex items-center h-8 px-3 rounded-md bg-background text-[13px] text-muted-foreground shrink-0"
          style={{ border: '1px solid var(--input)' }}
        >
          {formatDateRange(dateFrom, dateTo)}
        </div>

        {/* View by */}
        <Select
          value={viewBy}
          onValueChange={(v) => { setViewBy(v as ViewBy); setPage(1); }}
        >
          <SelectTrigger className="h-8 text-[13px] w-auto min-w-[130px]">
            <SelectValue placeholder="View by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="individual">Individual</SelectItem>
            {teamsEnabled && <SelectItem value="team">Planner Group</SelectItem>}
            <SelectItem value="department">Department</SelectItem>
            <SelectItem value="location">Location</SelectItem>
          </SelectContent>
        </Select>

        {/* Availability threshold */}
        <Segmented
          options={[
            { label: 'Any',    value: 'any'  },
            { label: '20%+',   value: '20'   },
            { label: '40%+',   value: '40'   },
            { label: '60%+',   value: '60'   },
            { label: 'Fully',  value: 'full' },
          ]}
          value={availThreshold}
          onChange={(v) => { setAvailThreshold(v as AvailabilityThreshold); setPage(1); }}
        />

        <div className="ml-auto flex items-center gap-2">
          {/* Reset — only when secondary filters are active */}
          {(selectedDepartments.length > 0 || selectedTeams.length > 0 || selectedRoles.length > 0 || selectedSkills.length > 0 || selectedLocations.length > 0) && (
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <X className="h-3 w-3" />
              Reset
            </button>
          )}

          {/* Filter button */}
          <Button
            variant="outline"
            size="icon"
            className="relative h-8 w-8 shrink-0 bg-background"
            onClick={() => setFilterDrawerOpen(true)}
            aria-label="Open filters"
          >
            <Filter className="h-4 w-4" />
            {(() => {
              const count =
                (selectedDepartments.length > 0 ? 1 : 0) +
                (selectedTeams.length > 0 ? 1 : 0) +
                (selectedRoles.length > 0 ? 1 : 0) +
                (selectedSkills.length > 0 ? 1 : 0) +
                (selectedLocations.length > 0 ? 1 : 0);
              return count > 0 ? (
                <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                  {count}
                </span>
              ) : null;
            })()}
          </Button>

          {/* Column config button */}
          <Button
            variant="outline"
            size="icon"
            className="relative h-8 w-8 shrink-0 bg-background"
            onClick={() => setColConfigOpen(true)}
            aria-label="Configure columns"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {visibleCapacityCols.size < ALL_CAPACITY_COLS.length && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                {ALL_CAPACITY_COLS.length - visibleCapacityCols.size}
              </span>
            )}
          </Button>

          {/* Export to Excel */}
          {canExport && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0 bg-background"
                  onClick={handleExportExcel}
                  aria-label="Export available capacity report to Excel"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export to Excel</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          )}
        </div>
      </div>

      {/* ── Column config sheet ─────────────────────────────────────────── */}
      <Sheet open={colConfigOpen} onOpenChange={setColConfigOpen}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Configure columns</SheetTitle>
          <SheetDescription className="sr-only">Toggle columns on or off for the available capacity table.</SheetDescription>
          <SheetHeader className="px-6 pt-6 pb-5 border-b shrink-0">
            <div className="flex items-center gap-3 pr-8">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold leading-tight">Customise Columns</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Toggle columns on or off to personalise the table.</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums shrink-0">{visibleCapacityCols.size}</div>
                <span className="text-sm text-muted-foreground">of {ALL_CAPACITY_COLS.length} columns showing</span>
              </div>
              <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground" onClick={resetCapacityCols}>
                <RotateCcw className="h-3.5 w-3.5" />Reset defaults
              </Button>
            </div>
          </SheetHeader>
          <ScrollArea className="flex-1 min-h-0">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-2">
                {ALL_CAPACITY_COLS.map((col) => {
                  const isVisible = visibleCapacityCols.has(col.key);
                  return (
                    <div key={col.key} className={['flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors duration-150', isVisible ? 'bg-card border-border hover:border-border/80 hover:bg-accent/30' : 'bg-muted/10 border-border/30'].join(' ')}>
                      <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/30" />
                      <div className="flex-1 min-w-0">
                        <p className={['text-sm font-medium leading-snug', !isVisible ? 'text-muted-foreground' : 'text-foreground'].join(' ')}>{col.label}</p>
                        <p className="text-xs text-muted-foreground/60 mt-0.5 truncate leading-tight">{col.description}</p>
                      </div>
                      <Switch checked={isVisible} onCheckedChange={() => toggleCapacityCol(col.key)} aria-label={`${isVisible ? 'Hide' : 'Show'} ${col.label} column`} />
                    </div>
                  );
                })}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
          <SheetFooter className="px-4 py-3 border-t shrink-0">
            <Button className="w-full" onClick={() => setColConfigOpen(false)}>Done</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Filter drawer ─────────────────────────────────────────────────── */}
      <Sheet open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <SheetContent side="right" className="w-[380px] sm:max-w-[380px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Available capacity filters</SheetTitle>
          <SheetDescription className="sr-only">Filter by department, planner group, role, skills and location.</SheetDescription>
          {/* Header */}
          <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0 gap-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">Filters</span>
              {(() => {
                const count = (selectedDepartments.length > 0 ? 1 : 0) + (selectedTeams.length > 0 ? 1 : 0) + (selectedRoles.length > 0 ? 1 : 0) + (selectedSkills.length > 0 ? 1 : 0) + (selectedLocations.length > 0 ? 1 : 0);
                return (
                  <Badge variant="secondary" className="flex items-center gap-1 px-2.5 py-0.5 h-6 text-[11px] font-medium rounded-full bg-primary/10 text-primary border-0 hover:bg-primary/10">
                    <Filter className="h-3 w-3" />
                    {count} {count === 1 ? 'filter' : 'filters'} active
                  </Badge>
                );
              })()}
            </div>
          </SheetHeader>
          {/* Body */}
          <ScrollArea className="flex-1">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Department</label>
                  <MultiSelect options={departmentOptions} placeholder="All departments" value={selectedDepartments} onValueChange={(v) => { setSelectedDepartments(v); setPage(1); }} maxDisplay={2} />
                </div>
                {teamsEnabled && (
                  <>
                    <Separator />
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Planner Group</label>
                      <MultiSelect options={teamOptions} placeholder="All planner groups" value={selectedTeams} onValueChange={(v) => { setSelectedTeams(v); setPage(1); }} maxDisplay={2} />
                    </div>
                  </>
                )}
                <Separator />
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Role</label>
                  <MultiSelect options={roleOptions} placeholder="All roles" value={selectedRoles} onValueChange={(v) => { setSelectedRoles(v); setPage(1); }} maxDisplay={2} />
                </div>
                <Separator />
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Skills</label>
                  <MultiSelect options={skillOptions} placeholder="All skills" value={selectedSkills} onValueChange={(v) => { setSelectedSkills(v); setPage(1); }} maxDisplay={2} />
                </div>
                <Separator />
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Location</label>
                  <MultiSelect options={locationOptions} placeholder="All locations" value={selectedLocations} onValueChange={(v) => { setSelectedLocations(v); setPage(1); }} maxDisplay={2} />
                </div>
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
          {/* Footer */}
          <SheetFooter className="px-4 py-3 border-t shrink-0 flex-row items-center justify-between gap-2">
            <Button
              variant="ghost"
              className="h-9 gap-1.5 text-muted-foreground hover:text-foreground px-2"
              onClick={() => { handleReset(); setFilterDrawerOpen(false); }}
              disabled={(selectedDepartments.length + selectedTeams.length + selectedRoles.length + selectedSkills.length + selectedLocations.length) === 0}
            >
              <X className="h-3.5 w-3.5" />
              Clear All
            </Button>
            <Button className="h-9 gap-1.5 px-5" onClick={() => setFilterDrawerOpen(false)}>
              <Check className="h-3.5 w-3.5" />
              Apply Filters
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Scrollable content area ───────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">

          {isLiveDataLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Loading live capacity data…</p>
            </div>
          ) : liveBookingsError ? (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
              <AlertTriangle className="h-8 w-8 text-destructive" />
              <p className="text-sm font-medium text-foreground">Failed to load booking data</p>
              <p className="text-xs text-muted-foreground">{liveBookingsError}</p>
              <Button variant="outline" size="sm" onClick={() => fetchLiveBookings(dateFrom, dateTo)}>Retry</Button>
            </div>
          ) : data.isEmpty ? (
            <EmptyState onReset={handleReset} onBenchReport={onNavigateToBench} viewBy={viewBy} hasNoCompanyData={data.hasNoCompanyData} />
          ) : (
          <>
            {/* ── Stat cards ────────────────────────────────────────────── */}
            <div className="flex gap-3">
              {/* Available resources */}
              <StatCard
                value={`${data.summary.availableResources} of ${data.summary.totalResources}`}
                accent="teal"
                label="Available resources"
                subLabel="With at least some unbooked capacity"
              />
              {/* Total available hours */}
              <StatCard
                value={`${data.summary.totalAvailableHours}h`}
                label="Total available hours"
                subLabel={formatDateRange(dateFrom, dateTo)}
              />
              {/* Fully available */}
              <StatCard
                value={String(data.summary.fullyAvailable)}
                accent="teal"
                label="Fully available resources"
                subLabel="0% booked in this period"
                isFilterable
                isActive={activeCardFilter === 'fully-available'}
                onClick={() => {
                  setActiveCardFilter((prev) => prev === 'fully-available' ? null : 'fully-available');
                  setPage(1);
                }}
              />
              {/* Skills with no availability */}
              <StatCard
                value={String(data.skillsWithNoAvailability.length)}
                accent={data.skillsWithNoAvailability.length > 0 ? 'destructive' : 'default'}
                label="Skills with no availability"
                subLabel={
                  data.skillsWithNoAvailability.length > 0
                    ? 'Click to scroll to skills gap section'
                    : 'All skills have available resources'
                }
                isFilterable={data.skillsWithNoAvailability.length > 0}
                isActive={false}
                onClick={
                  data.skillsWithNoAvailability.length > 0
                    ? () => skillsGapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    : undefined
                }
              />
            </div>

            {/* ── Active card filter pill ───────────────────────────────── */}
            {activeCardFilter && (
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-muted-foreground">Filtered by:</span>
                <button
                  type="button"
                  onClick={() => { setActiveCardFilter(null); setPage(1); }}
                  className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                >
                  {activeCardFilter === 'fully-available' && 'Fully available resources'}
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className="shrink-0">
                    <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
                <span className="text-[11px] text-muted-foreground">
                  — showing {cardFilteredRows.length} of {data.rows.length} resources
                </span>
              </div>
            )}

            {/* ── Horizontal bar chart ──────────────────────────────────── */}
            <div
              className="rounded-[var(--radius-lg)] p-4 flex flex-col gap-3"
              style={{
                background: 'var(--background)',
                border: '0.5px solid var(--color-border-tertiary, var(--border))',
              }}
            >
              {/* Chart toolbar */}
              <div className="flex items-center justify-between gap-3 flex-wrap shrink-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[12px] font-medium text-foreground">
                    {chartData.length} of {displayGrouped ? data.groupedRows.length : cardFilteredRows.length}{' '}
                    {displayGrouped ? groupLabel[viewBy].toLowerCase() + 's' : 'resources'}
                  </span>
                  {(displayGrouped ? data.groupedRows.length : cardFilteredRows.length) > chartData.length && (
                    <span className="text-[11px] text-muted-foreground">
                      · full data in table below
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Sort by</span>
                    <Select
                      value={chartSortBy}
                      onValueChange={(v) => setChartSortBy(v as typeof chartSortBy)}
                    >
                      <SelectTrigger className="h-7 text-[12px] w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available-desc">Available % (high → low)</SelectItem>
                        <SelectItem value="available-asc">Available % (low → high)</SelectItem>
                        <SelectItem value="name">Name (A–Z)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground whitespace-nowrap">Show</span>
                    <Select
                      value={String(chartShowLimit)}
                      onValueChange={(v) => setChartShowLimit(Number(v))}
                    >
                      <SelectTrigger className="h-7 text-[12px] w-[72px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="-1">All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Horizontal stacked bar chart — resource rows */}
              <div className="relative overflow-hidden" ref={chartScrollRef}>
                <div className="overflow-y-auto overflow-x-hidden" style={{ maxHeight: 360 }}>
                  <div style={{ width: '100%', height: chartInnerHeight }}>
                    <ResponsiveContainer width="100%" height={chartInnerHeight}>
                    <BarChart
                      layout="vertical"
                      data={chartData}
                      margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
                      barSize={BAR_HEIGHT}
                    >
                      <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis
                        type="number"
                        domain={[0, 100]}
                        tickFormatter={(v) => `${v}%`}
                        tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={X_AXIS_WIDTH}
                        tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(name: string) => name.length > 14 ? `${name.slice(0, 13)}…` : name}
                      />
                      <RechartsTooltip
                        content={<CapacityChartTooltip />}
                        cursor={{ fill: 'var(--accent)', opacity: 0.3 }}
                      />
                      {/* Stacked: confirmed (teal), planned (light blue), available (grey-dashed look via low opacity) */}
                      <Bar dataKey="confirmed" name="confirmed" stackId="cap" radius={[0, 0, 0, 0]} fill={TEAL()} />
                      <Bar dataKey="planned"   name="planned"   stackId="cap" fill={settings.statusConfigs?.find(s => String(s.id) === 'planned')?.color || PLANNED_BLUE_FALLBACK()} />
                      <Bar dataKey="available" name="available" stackId="cap" radius={[0, 4, 4, 0]}>
                        {chartData.map((_, idx) => (
                          <Cell
                            key={idx}
                            fill={AVAIL_GREY()}
                            fillOpacity={0.25}
                            stroke={AVAIL_GREY()}
                            strokeWidth={0.5}
                            strokeDasharray="3 2"
                          />
                        ))}
                      </Bar>
                    </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-4 px-1">
                <LegendSwatch colour={TEAL()}         label="Confirmed booked" />
                <LegendSwatch colour={settings.statusConfigs?.find(s => String(s.id) === 'planned')?.color || PLANNED_BLUE_FALLBACK()} label="Planned booked" />
                <LegendSwatch colour={AVAIL_GREY()}   label="Available (unbooked)" dashed />
              </div>
            </div>

            {/* ── Skills gap section ────────────────────────────────────── */}
            {data.skillsWithNoAvailability.length > 0 && (
              <div
                ref={skillsGapRef}
                className="rounded-[var(--radius-lg)] p-4 flex flex-col gap-3"
                style={{
                  background: 'var(--background)',
                  border: '0.5px solid var(--color-border-tertiary, var(--border))',
                }}
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-destructive shrink-0" aria-hidden="true" />
                  <p className="text-[13px] font-semibold text-foreground">
                    Skills gap — no available resources in this period
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.skillsWithNoAvailability.map((skill) => (
                    <span
                      key={skill}
                      className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[12px] font-medium bg-destructive/10 text-destructive border border-destructive/20"
                    >
                      {skill}
                      <span className="text-[11px] opacity-70">· 0 available</span>
                    </span>
                  ))}
                </div>
                {onNavigateToBench && (
                  <button
                    onClick={onNavigateToBench}
                    className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground hover:underline self-start cursor-pointer"
                  >
                    View bench report ›
                  </button>
                )}
              </div>
            )}

            {/* ── Data table ────────────────────────────────────────────── */}
            <div
              className="rounded-[var(--radius-lg)]"
              style={{ border: '0.5px solid var(--color-border-tertiary, var(--border))' }}
            >
              <div className="overflow-x-auto rounded-[var(--radius-lg)]">
                <table className="w-full border-collapse text-[13px] table-fixed">
                  {/* ── Colgroup: pinned widths keep headers & cells in sync ── */}
                  {displayGrouped ? (
                    <colgroup>
                      <col style={{ width: 220 }} />{/* Group name  */}
                      <col style={{ width: 100 }} />{/* Resources   */}
                      <col style={{ width: 260 }} />{/* Skills      */}
                      <col style={{ width: 100 }} />{/* Avail. hrs  */}
                      <col style={{ width:  90 }} />{/* Avail. %    */}
                      <col style={{ width: 110 }} />{/* Confirmed % */}
                      <col style={{ width: 100 }} />{/* Planned %   */}
                    </colgroup>
                  ) : (
                    <colgroup>
                      <col style={{ width: 200 }} />{/* Resource       */}
                      <col style={{ width: 130 }} />{/* Department     */}
                      <col style={{ width: 140 }} />{/* Role           */}
                      <col style={{ width: 120 }} />{/* Location       */}
                      <col style={{ width: 220 }} />{/* Skills         */}
                      <col style={{ width: 130 }} />{/* Available from */}
                      <col style={{ width:  95 }} />{/* Avail. hrs     */}
                      <col style={{ width:  85 }} />{/* Avail. %       */}
                      <col style={{ width: 105 }} />{/* Confirmed %    */}
                      <col style={{ width:  95 }} />{/* Planned %      */}
                      <col style={{ width: 100 }} />{/* Action         */}
                    </colgroup>
                  )}
                  <thead>
                    <tr style={{ background: 'var(--color-background-secondary, var(--muted))' }}>
                      {displayGrouped ? (
                        <>
                          <SortHeader label={groupLabel[viewBy]} col="name"          sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                          <SortHeader label="Resources"          col="available-hrs" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                          <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left">Skills</th>
                          <SortHeader label="Avail. hrs"         col="available-hrs" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                          <SortHeader label="Avail. %"           col="available-pct" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                          <SortHeader label="Confirmed %"        col="confirmed-pct" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                          <SortHeader label="Planned %"          col="planned-pct"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                        </>
                      ) : (
                        <>
                          <SortHeader label="Resource"       col="name"           sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                          {visibleCapacityCols.has('department') && <SortHeader label="Department"     col="department"     sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />}
                          {visibleCapacityCols.has('role') && <SortHeader label="Role"           col="role"           sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />}
                          {visibleCapacityCols.has('location') && <SortHeader label="Location"       col="location"       sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />}
                          {visibleCapacityCols.has('availableHrs') && <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left">Skills</th>}
                          {visibleCapacityCols.has('availableFrom') && <SortHeader label="Available from" col="available-from" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />}
                          {visibleCapacityCols.has('availableHrs') && <SortHeader label="Avail. hrs"     col="available-hrs"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                          {visibleCapacityCols.has('availablePct') && <SortHeader label="Avail. %"       col="available-pct"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                          {visibleCapacityCols.has('confirmedPct') && <SortHeader label="Confirmed %"    col="confirmed-pct"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                          {visibleCapacityCols.has('plannedPct') && <SortHeader label="Planned %"      col="planned-pct"    sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />}
                          <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-center sticky right-0 bg-[var(--color-background-secondary,var(--muted))]">Action</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((rawRow, idx) => {
                      const rowBg = idx % 2 === 0 ? 'var(--background)' : 'var(--color-background-secondary, var(--muted))';
                      const hoverBg = 'var(--teal-50)';

                      // ── Grouped row
                      if (displayGrouped) {
                        const g = rawRow as GroupedCapacityRow;
                        return (
                          <tr
                            key={g.groupKey}
                            style={{ height: 52, background: rowBg }}
                            className="transition-colors"
                            onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = hoverBg; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = rowBg; }}
                          >
                            {/* Group name */}
                            <td className="px-3 py-2">
                              <p className="font-medium text-[13px] text-foreground truncate">{g.groupKey}</p>
                            </td>
                            {/* Resources count */}
                            <td className="px-3 py-2 text-right text-[13px] text-foreground whitespace-nowrap">
                              <span className="font-medium text-teal-600">{g.availableResourceCount}</span>
                              <span className="text-muted-foreground"> / {g.resourceCount}</span>
                            </td>
                            {/* Skills */}
                            <td className="px-3 py-2">
                              <div className="flex flex-wrap gap-1">
                                {g.skills.slice(0, 3).map((skill) => (
                                  <Badge key={skill} variant="outline" className="text-[10px] px-1.5 py-0 text-foreground whitespace-nowrap">{skill}</Badge>
                                ))}
                                {g.skills.length > 3 && (
                                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">+{g.skills.length - 3} more</span>
                                )}
                              </div>
                            </td>
                            {/* Available hours */}
                            <td className="px-3 py-2 text-right text-foreground font-medium text-[13px]">{g.availableHours}h</td>
                            {/* Available % */}
                            <td className="px-3 py-2 text-right"><AvailPctBadge pct={g.availablePct} /></td>
                            {/* Confirmed % */}
                            <td className="px-3 py-2 text-right"><BookedPctBadge pct={g.confirmedBookedPct} variant="confirmed" /></td>
                            {/* Planned % */}
                            <td className="px-3 py-2 text-right"><BookedPctBadge pct={g.plannedPct} variant="planned" /></td>
                          </tr>
                        );
                      }

                      // ── Individual row
                      const row = rawRow as CapacityRow;
                      return (
                      <tr
                        key={row.id}
                        onClick={() => setDrawerRow(row)}
                        className="cursor-pointer transition-colors"
                        style={{
                          height: 52,
                          background: rowBg,
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background = hoverBg;
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background = rowBg;
                        }}
                      >
                        {/* Resource */}
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2.5">
                            <Avatar className="h-7 w-7 shrink-0">
                              <AvatarImage src={row.photoUrl} alt={row.name} />
                              <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                                {row.initials}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="font-medium text-[13px] text-foreground truncate leading-tight">
                                {row.name}
                              </p>
                              <p className="text-[11px] text-muted-foreground truncate leading-tight">
                                {row.role}
                              </p>
                            </div>
                          </div>
                        </td>
                        {/* Department */}
                        {visibleCapacityCols.has('department') && (
                          <td className="px-3 py-2 text-foreground whitespace-nowrap text-[13px]">
                            {row.department}
                          </td>
                        )}
                        {/* Role */}
                        {visibleCapacityCols.has('role') && (
                          <td className="px-3 py-2 whitespace-nowrap">
                            <p className="text-[13px] text-foreground">{row.role}</p>
                          </td>
                        )}
                        {/* Location */}
                        {visibleCapacityCols.has('location') && (
                          <td className="px-3 py-2 text-[13px] text-foreground whitespace-nowrap">
                            {row.location}
                          </td>
                        )}
                        {/* Skills — up to 3 tags + "more" link */}
                        {visibleCapacityCols.has('availableHrs') && (
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {row.skills.slice(0, 3).map((skill) => (
                              <Badge
                                key={skill}
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 text-foreground whitespace-nowrap"
                              >
                                {skill}
                              </Badge>
                            ))}
                            {row.skills.length > 3 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setDrawerRow(row); }}
                                className="text-[10px] text-primary hover:underline whitespace-nowrap"
                              >
                                +{row.skills.length - 3} more
                              </button>
                            )}
                          </div>
                        </td>
                        )}
                        {/* Available from */}
                        {visibleCapacityCols.has('availableFrom') && (
                          <td className="px-3 py-2">
                            <AvailableFromCell date={row.availableFromDate} />
                          </td>
                        )}
                        {/* Available hours */}
                        {visibleCapacityCols.has('availableHrs') && (
                          <td className="px-3 py-2 text-right text-foreground font-medium text-[13px]">
                            {row.availableHours}h
                          </td>
                        )}
                        {/* Available % */}
                        {visibleCapacityCols.has('availablePct') && (
                          <td className="px-3 py-2 text-right">
                            <AvailPctBadge pct={row.availablePct} />
                          </td>
                        )}
                        {/* Confirmed % */}
                        {visibleCapacityCols.has('confirmedPct') && (
                          <td className="px-3 py-2 text-right">
                            <BookedPctBadge pct={row.confirmedBookedPct} variant="confirmed" />
                          </td>
                        )}
                        {/* Planned % */}
                        {visibleCapacityCols.has('plannedPct') && (
                          <td className="px-3 py-2 text-right">
                            <BookedPctBadge pct={row.plannedPct} variant="planned" />
                          </td>
                        )}
                        {/* Action — sticky column */}
                        <td
                          className="px-3 py-2 text-center sticky right-0"
                          style={{ background: rowBg }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLTableCellElement).style.background = hoverBg;
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLTableCellElement).style.background = rowBg;
                          }}
                        >
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[12px] px-3 whitespace-nowrap"
                            onClick={(e) => handleRequestBooking(row, e)}
                          >
                            Request
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>

                  {/* Footer totals row */}
                  {footerTotals && (
                    <tfoot>
                      <tr
                        style={{
                          background: 'var(--color-background-secondary, var(--muted))',
                          borderTop: '0.5px solid var(--color-border-primary, var(--border))',
                        }}
                      >
                        {displayGrouped ? (
                          // Grouped mode: 7 columns — name | resources | skills | avail.hrs | avail.% | confirmed% | planned%
                          <>
                            <td
                              className="px-3 py-2.5 font-medium text-[13px] text-foreground"
                              colSpan={3}
                            >
                              Total / average
                            </td>
                            <td className="px-3 py-2.5 text-right font-medium text-[13px] text-foreground">
                              {footerTotals.totalAvailHours}h
                            </td>
                            <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                              <AvailPctBadge pct={footerTotals.avgAvailPct} />
                            </td>
                            <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                              <BookedPctBadge pct={footerTotals.avgConfirmedPct} variant="confirmed" />
                            </td>
                            <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                              <BookedPctBadge pct={footerTotals.avgPlannedPct} variant="planned" />
                            </td>
                          </>
                        ) : (
                          // Individual mode: 12 columns — resource | dept | role | location | skills | avail-from | avail.hrs | avail.% | confirmed% | planned% | day rate | action
                          <>
                            <td
                              className="px-3 py-2.5 font-medium text-[13px] text-foreground"
                              colSpan={6}
                            >
                              Total / average
                            </td>
                            <td className="px-3 py-2.5 text-right font-medium text-[13px] text-foreground">
                              {footerTotals.totalAvailHours}h
                            </td>
                            <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                              <AvailPctBadge pct={footerTotals.avgAvailPct} />
                            </td>
                            <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                              <BookedPctBadge pct={footerTotals.avgConfirmedPct} variant="confirmed" />
                            </td>
                            <td className="px-3 py-2.5 text-right font-medium text-[13px]">
                              <BookedPctBadge pct={footerTotals.avgPlannedPct} variant="planned" />
                            </td>
                            <td colSpan={1} />
                          </>
                        )}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* ── Pagination ─────────────────────────────────────────────── */}
            <div className="flex items-center justify-between shrink-0 pb-2">
              <p className="text-[13px] text-muted-foreground">
                Showing {Math.min((page - 1) * pageSize + 1, sortedRows.length)}–
                {Math.min(page * pageSize, sortedRows.length)} of {sortedRows.length} resources
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 text-[13px]"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </Button>
                <span className="text-[13px] text-muted-foreground px-1">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-8 text-[13px]"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-muted-foreground">Rows per page:</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}
                >
                  <SelectTrigger className="h-8 w-20 text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Resource detail drawer ────────────────────────────────────────── */}
      <ResourceDetailDrawer
        row={drawerRow}
        open={drawerRow !== null}
        onClose={() => setDrawerRow(null)}
        onNavigateToCalendar={onNavigateToCalendar}
      />

      {/* ── Add Booking modal — pre-populated with selected resource ──────── */}
      <ComprehensiveBookingModal
        open={bookingModalOpen}
        onOpenChange={(o) => {
          setBookingModalOpen(o);
        }}
        onSave={async (booking) => {
          await addBooking(booking);
          setBookingModalOpen(false);
          toast.success('Booking saved');
          // Navigate back to the originating report if requested
          if (returnContext?.returnToPage) {
            onNavigate?.(returnContext.returnToPage);
          }
        }}
        onUpdate={async (id, booking) => { await updateBooking(id as string, booking); }}
        onDelete={(booking) => { deleteBooking(booking.id); setBookingModalOpen(false); }}
        resources={resources}
        projectDetails={projectDetails}
        existingBookings={bookings}
        preselectedResource={bookingResource ?? undefined}
        preselectedBookingData={returnContext ? {
          projectId: returnContext.projectId,
          requiredRole: returnContext.roleName,
        } : undefined}
        mode="create"
      />
    </div>
  );
}
